/**
 * 极简三消引擎：纯状态、无 DOM / uni。
 * 5～10 阶、6 色、三连消除、下落补子、级联计分。
 * 交换结果同时携带动画帧，运行端可依次播放交换、消除和下落。
 */

export const MATCH3_SIZE = 8

export const MATCH3_GEMS = [
  { id: 0, symbol: '♥', color: '#ff668a' },
  { id: 1, symbol: '◆', color: '#59a9ff' },
  { id: 2, symbol: '●', color: '#66d4a4' },
  { id: 3, symbol: '★', color: '#ffc85b' },
  { id: 4, symbol: '✦', color: '#a98bff' },
  { id: 5, symbol: '☾', color: '#ff9468' }
]

function randomGemId() {
  return Math.floor(Math.random() * MATCH3_GEMS.length)
}

export function gemMeta(id) {
  const n = parseInt(id, 10)
  if (!Number.isFinite(n) || n < 0 || n >= MATCH3_GEMS.length) return MATCH3_GEMS[0]
  return MATCH3_GEMS[n]
}

function markMatches(board) {
  const size = board.length
  const marked = []
  for (let r = 0; r < size; r++) {
    marked[r] = []
    for (let c = 0; c < size; c++) marked[r][c] = false
  }
  for (let r = 0; r < size; r++) {
    let run = 1
    for (let c = 1; c <= size; c++) {
      const same = c < size && board[r][c] >= 0 && board[r][c] === board[r][c - 1]
      if (same) {
        run++
      } else {
        if (run >= 3) {
          for (let k = 1; k <= run; k++) marked[r][c - k] = true
        }
        run = 1
      }
    }
  }
  for (let c = 0; c < size; c++) {
    let run = 1
    for (let r = 1; r <= size; r++) {
      const same = r < size && board[r][c] >= 0 && board[r][c] === board[r - 1][c]
      if (same) {
        run++
      } else {
        if (run >= 3) {
          for (let k = 1; k <= run; k++) marked[r - k][c] = true
        }
        run = 1
      }
    }
  }
  return marked
}

export function boardHasMatch(board) {
  const marked = markMatches(board)
  const size = board.length
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (marked[r][c]) return true
    }
  }
  return false
}

function cloneBoard(board) {
  return board.map(row => row.slice())
}

function matchedCells(marked) {
  const cells = []
  for (let r = 0; r < marked.length; r++) {
    for (let c = 0; c < marked.length; c++) {
      if (marked[r][c]) cells.push({ r, c })
    }
  }
  return cells
}

function applyGravity(board) {
  const size = board.length
  const falling = []
  for (let c = 0; c < size; c++) {
    const stack = []
    for (let r = 0; r < size; r++) {
      if (board[r][c] >= 0) stack.push(board[r][c])
    }
    const empty = size - stack.length
    for (let r = 0; r < empty; r++) board[r][c] = randomGemId()
    for (let i = 0; i < stack.length; i++) board[empty + i][c] = stack[i]
    if (empty > 0) {
      for (let r = 0; r < size; r++) falling.push({ r, c, distance: empty })
    }
  }
  return falling
}

export function resolveMatches(board) {
  let scoreGain = 0
  let totalCleared = 0
  const steps = []
  let guard = 0
  while (guard < 48) {
    guard++
    const marked = markMatches(board)
    const matched = matchedCells(marked)
    let cleared = 0
    for (let r = 0; r < board.length; r++) {
      for (let c = 0; c < board.length; c++) {
        if (!marked[r][c]) continue
        board[r][c] = -1
        cleared++
      }
    }
    if (!cleared) break
    totalCleared += cleared
    scoreGain += cleared * 10
    const falling = applyGravity(board)
    steps.push({ matched, falling, afterGravity: cloneBoard(board) })
  }
  return { scoreGain, cleared: totalCleared, steps }
}

export function generateBoard(size) {
  const n = size || MATCH3_SIZE
  let board = []
  let tries = 0
  do {
    board = []
    for (let r = 0; r < n; r++) {
      board[r] = []
      for (let c = 0; c < n; c++) board[r][c] = randomGemId()
    }
    tries++
  } while (boardHasMatch(board) && tries < 80)
  if (boardHasMatch(board)) resolveMatches(board)
  return board
}

export function createMatch3State(size) {
  const n = Math.max(5, Math.min(10, parseInt(size, 10) || MATCH3_SIZE))
  return {
    size: n,
    board: generateBoard(n),
    selected: null,
    score: 0,
    cleared: 0,
    generation: 1
  }
}

export function restartMatch3(state, size) {
  const s = state && typeof state === 'object' ? state : createMatch3State(size)
  s.generation = (s.generation || 0) + 1
  s.size = Math.max(5, Math.min(10, parseInt(size, 10) || s.size || MATCH3_SIZE))
  s.board = generateBoard(s.size)
  s.selected = null
  s.score = 0
  s.cleared = 0
  return s
}

function isAdjacent(a, b) {
  return Math.abs(a.r - b.r) + Math.abs(a.c - b.c) === 1
}

export function swapMatch3(state, from, to) {
  const s = state
  const size = s && (s.size || MATCH3_SIZE)
  const a = { r: parseInt(from && from.r, 10), c: parseInt(from && from.c, 10) }
  const b = { r: parseInt(to && to.r, 10), c: parseInt(to && to.c, 10) }
  const validCell = cell => Number.isFinite(cell.r) && Number.isFinite(cell.c) && cell.r >= 0 && cell.c >= 0 && cell.r < size && cell.c < size
  if (!s || !validCell(a) || !validCell(b) || !isAdjacent(a, b)) return { changed: false }
  const original = s.board[a.r][a.c]
  s.board[a.r][a.c] = s.board[b.r][b.c]
  s.board[b.r][b.c] = original
  s.selected = null
  if (!boardHasMatch(s.board)) {
    s.board[b.r][b.c] = s.board[a.r][a.c]
    s.board[a.r][a.c] = original
    return { changed: true, invalid: true, from: a, to: b }
  }
  const swappedBoard = cloneBoard(s.board)
  const resolved = resolveMatches(s.board)
  s.score += resolved.scoreGain
  s.cleared = (s.cleared || 0) + resolved.cleared
  return { changed: true, matched: true, from: a, to: b, swappedBoard, finalBoard: cloneBoard(s.board), steps: resolved.steps, scoreGain: resolved.scoreGain, cleared: resolved.cleared }
}

export function tapMatch3(state, row, col) {
  const s = state
  const size = s.size || MATCH3_SIZE
  const r = parseInt(row, 10)
  const c = parseInt(col, 10)
  if (!Number.isFinite(r) || !Number.isFinite(c) || r < 0 || c < 0 || r >= size || c >= size) {
    return { changed: false }
  }
  if (!s.selected) {
    s.selected = { r, c }
    return { changed: true }
  }
  const sel = s.selected
  if (sel.r === r && sel.c === c) {
    s.selected = null
    return { changed: true }
  }
  if (!isAdjacent(sel, { r, c })) {
    s.selected = { r, c }
    return { changed: true }
  }
  return swapMatch3(s, sel, { r, c })
}

export function match3RowsForBoard(board, selected) {
  const size = board.length
  const rows = []
  for (let r = 0; r < size; r++) {
    const row = []
    for (let c = 0; c < size; c++) {
      const id = board[r] ? board[r][c] : 0
      const meta = gemMeta(id)
      row.push({
        key: r + '-' + c,
        r,
        c,
        gem: id,
        symbol: meta.symbol,
        color: meta.color,
        selected: !!(selected && selected.r === r && selected.c === c)
      })
    }
    rows.push(row)
  }
  return rows
}


export function match3RowsForView(state) {
  const s = state || createMatch3State()
  return match3RowsForBoard(s.board, s.selected)
}
